import { useNavigate } from 'react-router';
import textsData from '@/textData/IncidentDetailTexts.json'
import {
  Button,
  LoadingSpinner,
  RowSpacer,
  DebtBadge,
  BudgetStatusPill,
} from '../atoms';
import {
  GetIncidentQuery,
  IncidentStatusOption,
  UpdateIncidentMutationVariables,
  useManagerIncidentAppointmentSlotsQuery,
  useUpdateIncidentMutation,
  UserTypeOption,
} from '@/graphql/generated';
import {
  useAddPicturesFromIncidentFormModalContext,
  useAddSupplierFormModalContext,
  useErrorHandlerContext,
  useIncidentDetailContext,
  useOpeningElementsControllerContext,
  useUpdateStatusWithNoteModalContext,
  useUserLanguage,
} from '@/customHooks';
import {
  IncidentCategoryMapperWithIcon,
  IncidentPriorityMapperWithIcon,
  IncidentTypeMapperWithIcon,
  IncidentStatusMapperWithIcon,
  SmallInfoCard,
  RectangleInfoCard,
  IncidentPartyPersonCard,
  NormalFormSelectionField,
  NormalFormDateField,
} from '../molecules'
import {
  capitalizeFirstLetter,
  incidentStatusOptionMapper,
  priorityMapper,
  truncateDisplayId,
  advancedNormalizeDate,
} from '@/helpers';
import {
  AddSupplierFormModal,
  ChatWindowShower,
  AddPicturesFromIncidentFormModal,
  UpdateStatusWithNoteModal,
} from '../templates';
import { IncidentResponsibilityManager } from '../organism';
import claendarIcon from '@/assets/incidents-icons/calendar-icon.svg'
import houseIcon from '@/assets/incidents-icons/house-icon.svg'
import bedIcon from '@/assets/incidents-icons/bed-icon.svg'
import { pathURL } from '@/components/constants/urls';
import { ShowGalleryPictureModal } from '../templates/ShowGalleryPictureModal'
import { useShowGalleryModalContext } from '@/customHooks/useShowGalleryModalContext'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PriorityOption } from '@/graphql/generated';

type BudgetsListProps = {
  budgets: NonNullable<GetIncidentQuery['incident']['budgets']> | undefined
}

const BudgetsList = (props: BudgetsListProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.overview.budgetsList : textsData.en.overview.budgetsList
  const headers = userLanguage === 'es'
    ? { supplier: 'Proveedor', cost: 'Costo', subject: 'Asunto', status: 'Estado' }
    : { supplier: 'Supplier', cost: 'Cost', subject: 'Subject', status: 'Status' }

  if (!props.budgets || props.budgets.length === 0) {
    return <p>{texts.advice}</p>
  }

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-border-color-1 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-white">
            <th className="p-3 text-left font-bold">{headers.supplier}</th>
            <th className="p-3 text-left font-bold">{headers.cost}</th>
            <th className="p-3 text-left font-bold">{headers.subject}</th>
            <th className="p-3 text-left font-bold">{headers.status}</th>
          </tr>
        </thead>
        <tbody>
          {props.budgets.map((budget, index) => (
            <tr key={budget.id} className={index % 2 !== 0 ? 'bg-background2' : 'bg-white'}>
              <td className="border-b border-gray-100 p-3">
                {`${capitalizeFirstLetter(budget.supplier.firstName)} ${capitalizeFirstLetter(budget.supplier.lastName)}`}
              </td>
              <td className="border-b border-gray-100 p-3">{budget.budgetCost}</td>
              <td className="border-b border-gray-100 p-3">{budget.subject}</td>
              <td className="border-b border-gray-100 p-3">
                <BudgetStatusPill option={budget.budgetStatusOption} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type IncidentDetailOverviewProps = {
  openChat?: boolean;
}

export const IncidentDetailOverview = ({ openChat = false }: IncidentDetailOverviewProps) => {
  const userLanguage = useUserLanguage()
  const [isGalleryLoading, setIsGalleryLoading] = useState(false)
  const texts = userLanguage === 'es' ? textsData.es.overview : textsData.en.overview
  const noImagesLabel = userLanguage === 'es' ? 'No hay imágenes' : 'No images'

  const [updateStatus, { loading: updateStatusLoading }] = useUpdateIncidentMutation();

  const { error: errorHandle } = useErrorHandlerContext()

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { openModal: showPictureGallery } = useShowGalleryModalContext()

  const { openModal: openAddSupplierModal } = useAddSupplierFormModalContext()

  const { openModal: openAddPictureFormModal } = useAddPicturesFromIncidentFormModalContext()

  const { openModal: openUpdateStatusWithNoteModal } = useUpdateStatusWithNoteModalContext()

  const { incident, setIncident, downloadedImages, setDownloadedImages } = useIncidentDetailContext()
  const { data: managerAppointmentSlotsData } = useManagerIncidentAppointmentSlotsQuery({
    variables: { incidentId: incident?.id ?? '' },
    skip: !incident?.id,
  })

  const [editingField, setEditingField] = useState<'priority' | 'status' | 'endDate' | null>(null);

  const [imageUrls, setImageUrls] = useState<string[]>([])
  const picturesRowRef = useRef<HTMLDivElement | null>(null)

  const navigate = useNavigate()

  const onSaveField = <T extends keyof Omit<UpdateIncidentMutationVariables, 'incidentId'>>(
    field: T,
    value: UpdateIncidentMutationVariables[T]
  ) => {
    if (!incident) return;
    updateStatus({
      variables: {
        incidentId: incident.id,
        [field]: value,
      } as UpdateIncidentMutationVariables,
    })
      .then((response) => {
        confirmDialogModalOpen({
          title: texts.confirmationDialog.title,
          description: `${texts.confirmationDialog.description}. ID: ${response.data?.updateIncident.id}`,
        });
        setIncident(undefined);
        setEditingField(null);
      })
      .catch(errorHandle);
  };

  const rating = useMemo(() => {
    if (!incident?.incidentRating) return null

    const provitionValue = parseInt(incident.incidentRating.value, 10)

    if (isNaN(provitionValue) || provitionValue < 0 || provitionValue > 5) {
      console.warn(`Valor de rating no válido: ${incident.incidentRating.value}`)
      return { value: 0, comment: incident.incidentRating.comment ?? '' }
    }

    const comment = incident.incidentRating.comment ?? ''

    return { value: provitionValue, comment }
  }, [incident])

  const onClickOnCompleteButtonHandler = (option: IncidentStatusOption) => {
    if (!incident) return
    openUpdateStatusWithNoteModal({
      status: option,
      onConfirm: () => {
        updateStatus({
          variables: {
            incidentId: incident?.id,
            statusOption: option,
          }
        })
        .then((response) => {
          confirmDialogModalOpen({
            title: texts.confirmationDialog.title,
            description: `${texts.confirmationDialog.description}. ID: ${response.data?.updateIncident.id}`
          })

          setIncident(undefined);
        })
        .catch(errorHandle)
      }
    })
  }

  const onSupplierCardClickHandle = () => {
    const toURL = `/${pathURL.USER}/${pathURL.SUPPLIER}/${pathURL.CREATE}`

    if (!incident || !incident.supplier) return

    navigate(toURL, {
      state: {
        supplierId: incident?.supplier.user.id,
        readOnly: true,
      }
    })
  }

  const onTenantCardClickHandle = () => {
    const toURL = `/${pathURL.USER}/${pathURL.TENANT}/${pathURL.CREATE}`

    if (!incident || !incident.tenant) return

    navigate(toURL, {
      state: {
        tenantId: incident.tenant.user.id,
        readOnly: true,
      }
    })
  }

  const onPropertyCardClickHandler = () => {
    const toURL = `/${pathURL.USER}/${pathURL.PROPERTY}/${pathURL.DETAIL}`

    if (!incident) return

    navigate(toURL, {
      state: {
        id: incident.property?.id
      }
    })
  }

  const priorityBgColor = useMemo(() => {
    switch (incident?.priorityOption) {
      case PriorityOption.Low:
        return 'bg-yellow-100'
      case PriorityOption.Medium:
        return 'bg-orange-100'
      case PriorityOption.High:
        return 'bg-red-100'
      default:
        return 'bg-white'
    }
  }, [incident?.priorityOption])

  const downloadImages = async () => {
    if (!incident?.fileUploads || incident.fileUploads.length === 0 || downloadedImages.length > 0) {
      return
    }

    try {
      setIsGalleryLoading(true)
      const imagePromises = incident.fileUploads.map(async (fileUpload) => {
        const response = await fetch(fileUpload.urlToDownload)
        if (!response.ok) {
          throw new Error(`Error al descargar la imagen: ${fileUpload.urlToDownload}`)
        }
        const blob = await response.blob()
        const filename = fileUpload.urlToDownload.substring(fileUpload.urlToDownload.lastIndexOf('/') + 1).split('?')[0]
        return new File([blob], filename, { type: blob.type })
      })

      const files = await Promise.all(imagePromises)
      setDownloadedImages(files)
    } catch (error) {
      errorHandle(error)
    } finally {
      setIsGalleryLoading(false)
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          downloadImages()
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (picturesRowRef.current) {
      observer.observe(picturesRowRef.current)
    }

    return () => {
      if (picturesRowRef.current) {
        observer.unobserve(picturesRowRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.fileUploads])

  useEffect(() => {
    if (downloadedImages.length === 0) return

    const urls = downloadedImages.map(file => URL.createObjectURL(file))
    setImageUrls(urls)

    return () => {
      urls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [downloadedImages])

  const openGalleryHandle = async () => {
    if (downloadedImages.length === 0) {
      console.log('No hay imágenes para mostrar.');
      return;
    }
    showPictureGallery(downloadedImages)
  }


  if (!incident) {
    return (
      <p><b>No incident info available</b></p>
    )
  }

  const startDateDisplay = incident.startDate
    ? advancedNormalizeDate(userLanguage, incident.startDate)
    : ''
  const endDateDisplay = incident.endDate
    ? advancedNormalizeDate(userLanguage, incident.endDate)
    : ''

  const locale = userLanguage === 'es' ? 'es-ES' : 'en-US'
  const formatDate = (value: string) => new Date(value).toLocaleDateString(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  const formatTime = (value: string) => new Date(value).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const selectedAppointmentSlot = managerAppointmentSlotsData?.managerIncidentAppointmentSlots.find((slot) => {
    const normalizedStatus = String(slot.status ?? '').toLowerCase()
    return normalizedStatus === 'selected'
  }) ?? managerAppointmentSlotsData?.managerIncidentAppointmentSlots.find((slot) => Boolean(slot.selectedAt))

  const appointmentDateTimeDisplay = selectedAppointmentSlot
    ? `${formatDate(selectedAppointmentSlot.startAt)}, ${formatTime(selectedAppointmentSlot.startAt)} - ${formatTime(selectedAppointmentSlot.endAt)}`
    : '-'

  return (
    <>
      <div className="space-y-6 pb-8 pt-2">
        <section className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between py-4">
          <div>
            <h1 className="text-2xl font-semibold">{texts.title}</h1>
            <p className="mt-1 text-sm text-[#666]">
              {texts.description}{' '}
              <span className="font-medium" title={incident.id}>
                ID: {truncateDisplayId(incident.id)}
              </span>
            </p>
            <p className="mt-1 text-sm text-[#666]">
              {`${texts.lastUpdated}: ${advancedNormalizeDate(userLanguage, incident.updatedAt)}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Button
              onClick={() => onClickOnCompleteButtonHandler(IncidentStatusOption.Cancelled)}
              isLoading={updateStatusLoading}
              secondary
            >
              {texts.buttons.button5}
            </Button>
            <Button
              onClick={() => onClickOnCompleteButtonHandler(IncidentStatusOption.Pending)}
              isLoading={updateStatusLoading}
              secondary
            >
              {texts.buttons.button4}
            </Button>
            <Button
              onClick={() => onClickOnCompleteButtonHandler(IncidentStatusOption.Completed)}
              isLoading={updateStatusLoading}
            >
              {texts.buttons.button1}
            </Button>
          </div>
        </section>

        <section className="py-4">
          <div className="grid grid-cols-1 items-stretch gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="mb-2 flex min-h-0 h-full lg:mb-0">
              <IncidentCategoryMapperWithIcon
                categoryOption={incident.incidentCategoryOption}
              />
            </div>
            <div className="mb-2 flex min-h-0 h-full lg:mb-0">
              <IncidentTypeMapperWithIcon
                typeOption={incident.incidentTypeOption}
              />
            </div>
            <div
              className={`mb-2 flex min-h-0 h-full cursor-pointer flex-col lg:mb-0 rounded-2xl transition-all duration-300 ${priorityBgColor} ${
                editingField === 'priority' ? 'ring-2 ring-gray-200 ring-offset-2' : 'ring-2 ring-transparent'
              }`}
              onClick={() => setEditingField(editingField === 'priority' ? null : 'priority')}
            >
              <IncidentPriorityMapperWithIcon 
                priorityOption={incident.priorityOption} 
                containerClassName={priorityBgColor}
              />
              {editingField === 'priority' && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="z-10 mt-2 rounded-lg bg-white p-4 shadow-box1 animate-in fade-in slide-in-from-top-1 duration-200"
                >
                  <NormalFormSelectionField<PriorityOption>
                    label={texts.updateForm.priorityOptionField.label}
                    name="priority"
                    options={Object.values(PriorityOption).map((val) => ({
                      name: priorityMapper(val, userLanguage),
                      value: val,
                    }))}
                    value={incident.priorityOption}
                    onChange={(val) => onSaveField('priorityOption', val)}
                  />
                </div>
              )}
            </div>
            <div
              className={`mb-2 flex min-h-0 h-full cursor-pointer flex-col lg:mb-0 rounded-2xl transition-all duration-300 ${
                editingField === 'status' ? 'ring-2 ring-gray-200 ring-offset-2' : 'ring-2 ring-transparent'
              }`}
              onClick={() => setEditingField(editingField === 'status' ? null : 'status')}
            >
              <IncidentStatusMapperWithIcon
                statusOption={incident.statusOption}
              />
              {editingField === 'status' && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="z-10 mt-2 rounded-lg bg-white p-4 shadow-box1 animate-in fade-in slide-in-from-top-1 duration-200"
                >
                  <NormalFormSelectionField<IncidentStatusOption>
                    label={texts.updateForm.statusOptionField.label}
                    name="status"
                    options={Object.values(IncidentStatusOption).map((val) => ({
                      name: incidentStatusOptionMapper(val, userLanguage),
                      value: val,
                    }))}
                    value={incident.statusOption}
                    onChange={(val) => onSaveField('statusOption', val)}
                  />
                </div>
              )}
            </div>
            <div className="mb-2 flex min-h-0 h-full lg:mb-0">
              <SmallInfoCard
                icon={claendarIcon}
                title={texts.statusCards.startDate}
                value={startDateDisplay || '-'}
              />
            </div>
            <div
              className={`mb-2 flex min-h-0 h-full cursor-pointer flex-col lg:mb-0 rounded-2xl transition-all duration-300 ${
                editingField === 'endDate' ? 'ring-2 ring-gray-200 ring-offset-2' : 'ring-2 ring-transparent'
              }`}
              onClick={() => setEditingField(editingField === 'endDate' ? null : 'endDate')}
            >
              <SmallInfoCard
                icon={claendarIcon}
                title={texts.statusCards.endDate}
                value={endDateDisplay || '-'}
              />
              {editingField === 'endDate' && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="z-10 mt-2 rounded-lg bg-white p-4 shadow-box1 animate-in fade-in slide-in-from-top-1 duration-200"
                >
                  <NormalFormDateField
                    label={texts.updateForm.endDateField.label}
                    name="endDate"
                    value={incident.endDate}
                    onChange={(val) => onSaveField('endDate', val)}
                  />
                </div>
              )}
            </div>
            <div className="mb-2 flex min-h-0 h-full lg:mb-0">
              <SmallInfoCard
                icon={claendarIcon}
                title={texts.statusCards.scheduledAppointment}
                value={appointmentDateTimeDisplay}
              />
            </div>
          </div>
        </section>

        <section className="py-4">
          <div className="md:pl-4">
            <h3 className="text-lg font-semibold">{texts.relatedInformationSection.title}</h3>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-12">
            <button
              type="button"
              className="w-full cursor-pointer border-none bg-transparent p-0 text-left"
              onClick={onPropertyCardClickHandler}
            >
              <RectangleInfoCard
                title={texts.relatedInformationSection.card2}
                subtitle={incident.property?.name ?? ''}
                iconSrc={houseIcon}
              />
            </button>
            <RectangleInfoCard
              title={texts.relatedInformationSection.card3}
              subtitle={incident.unity?.name ?? texts.noData.unity}
              iconSrc={bedIcon}
            />
          </div>
          <br />
          <div className="rounded-2xl bg-white p-6 shadow-box1 md:px-8 min-h-[90px]">
            <div className="border-b border-gray-200 pb-4">
              <h4 className="mb-4 text-sm">{texts.relatedInformationSection.card5}</h4>
              <p>{incident.subject ?? texts.relatedInformationSection.noSubject}</p>
            </div>
            <div className="pt-4">
              <h4 className="mb-4 text-sm">{texts.lastNoteSection.title}</h4>
              <p>{incident.notes?.[incident.notes?.length - 1]?.text ?? texts.lastNoteSection.noNoteAvailable}</p>
            </div>
          </div>
        </section>
        
        <section className="py-4">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <div className="flex justify-between align-middle">
                <div className="md:pl-4 mb-4">
                  <h3 className="text-lg font-semibold text-text-color">
                    {texts.assignPartiesSection.assigneeTitle}
                  </h3>
                </div>
                <button
                  className="bg-transparent border-none p-0 m-0 text-primary1 underline cursor-pointer hover:text-primary1/80 focus:outline-none"
                  onClick={() => openAddSupplierModal()}
                >
                  {texts.assignPartiesSection.changeSssignedButton}
                </button>
              </div>
              <IncidentPartyPersonCard
                displayName={
                  incident.supplier
                    ? `${capitalizeFirstLetter(incident.supplier.firstName)} ${capitalizeFirstLetter(incident.supplier.lastName)}`
                    : null
                }
                emptyLabel={texts.assignPartiesSection.noAssignee}
                onClick={
                  incident.supplier
                    ? onSupplierCardClickHandle
                    : () => openAddSupplierModal()
                }
              />
            </div>
            <div>
              <div className="md:pl-4 mb-4">
                <h3 className="text-lg font-semibold text-text-color">
                  {texts.assignPartiesSection.requestedByTitle}
                </h3>
              </div>
              <IncidentPartyPersonCard
                displayName={
                  incident.tenant
                    ? `${capitalizeFirstLetter(incident.tenant.firstName)} ${capitalizeFirstLetter(incident.tenant.lastName)}`
                    : null
                }
                emptyLabel={texts.assignPartiesSection.noRequester}
                onClick={incident.tenant ? onTenantCardClickHandle : undefined}
                debtBadge={
                  incident.tenant ? (
                    <DebtBadge hasDebt={incident.tenant.hasDebt ?? false} />
                  ) : undefined
                }
              />
            </div>
          </div>
        </section>
       
        <section className="py-4">
          <div className="pb-4 md:pl-4">
            <h3 className="text-lg font-semibold">{texts.responsibility.title}</h3>
          </div>
          <IncidentResponsibilityManager
            initialResponsibilities={{
              [UserTypeOption.Tenant]: Number(incident.incidentResponsabilities?.find(r => r.userTypeOption === UserTypeOption.Tenant)?.responsabilityPercentage || 0),
              [UserTypeOption.Owner]: Number(incident.incidentResponsabilities?.find(r => r.userTypeOption === UserTypeOption.Owner)?.responsabilityPercentage || 0),
              [UserTypeOption.Manager]: Number(incident.incidentResponsabilities?.find(r => r.userTypeOption === UserTypeOption.Manager)?.responsabilityPercentage || 0),
              [UserTypeOption.Supplier]: Number(incident.incidentResponsabilities?.find(r => r.userTypeOption === UserTypeOption.Supplier)?.responsabilityPercentage || 0),
            }}
            isLoading={updateStatusLoading}
            onUpdate={(data) => {
              updateStatus({
                variables: {
                  incidentId: incident.id,
                  ...data,
                },
              })
                .then((response) => {
                  confirmDialogModalOpen({
                    title: texts.confirmationDialog.title,
                    description: `${texts.confirmationDialog.description}. ID: ${response.data?.updateIncident.id}`,
                  });
                  setIncident(undefined);
                })
                .catch(errorHandle);
            }}
          />
        </section>

        <section className="py-4">
          <div className="pb-4 md:pl-4">
            <h3 className="text-lg font-semibold">{texts.tenantRatingSection.title}</h3>
          </div>
          <div className="mb-4 rounded-base bg-white p-6 shadow-box1">
          {rating !== null
            ? (
              <>
                <p className="my-2 text-sm text-[#555]">
                  <strong>{texts.tenantRatingSection.ratingLabel}:</strong>
                </p>
                <div className="my-2 flex flex-wrap items-center gap-2 text-2xl text-primary1">
                  {Array.from({ length: 5 }, (_, index) => (
                    <span key={index}>
                      {index < rating.value ? '★' : '☆'}
                    </span>
                  ))}
                  <span className="ml-2 text-base text-[#666]">
                    ({rating.value}/5)
                  </span>
                </div>
                {rating.comment && (
                  <>
                    <p className="my-2 text-sm text-[#555]">
                      <strong>{texts.tenantRatingSection.commentLabel}:</strong>
                    </p>
                    <p className="my-2 text-sm text-[#555]">{rating.comment}</p>
                  </>
                )}
              </>
            )
            : <p>{texts.tenantRatingSection.noRating}</p>
          }
          </div>
        </section>

        <section className="py-4">
          <div className="pb-4 md:pl-4">
            <h3 className="text-lg font-semibold">{texts.pictureGallerySection.title}</h3>
          </div>
          <div className="mb-4 rounded-base bg-white p-6 shadow-box1">
            <div>
            {
              isGalleryLoading
                ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                )
                : (
                  <div ref={picturesRowRef}>
                    <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
                      {
                        imageUrls.length > 0
                          ? (
                            imageUrls.slice(-4).map((url, index) => (
                              <div
                                key={index}
                                className="overflow-hidden rounded-base shadow-[0_5px_5px_rgba(0,0,0,0.2)]"
                              >
                                <img src={url} alt={`thumbnail ${index + 1}`} className="aspect-[3/2] h-full w-full object-cover" />
                              </div>
                            ))
                          )
                          : <p className="py-4 text-sm">{noImagesLabel}</p>
                      }
                    </div>
                  </div>
                )
            }
          </div>
          <div className="flex flex-wrap items-end justify-end gap-3 mt-4">
            <Button
              onClick={() => openAddPictureFormModal([])}
              disabled={isGalleryLoading}
              secondary
            >
              {texts.pictureGallerySection.button1}
            </Button>
            <Button
              onClick={() => openGalleryHandle()}
              isLoading={isGalleryLoading}
            >
              {texts.pictureGallerySection.button2}
            </Button>
          </div>
        </div>
        </section>

        <section>
          <div className="pb-4 md:pl-4">
            <h3 className="text-lg font-semibold">{texts.budgetsSection.title}</h3>
          </div>
          <div className="mb-4 rounded-base bg-white p-6 shadow-box1">
            <BudgetsList budgets={
              incident.selectedBudget
                ? Array(incident.selectedBudget)
                : []
            } />
          </div>
        </section>

        <RowSpacer height={3} />
      </div>
      <ChatWindowShower incidentId={incident.id} autoOpen={openChat} />
      <AddSupplierFormModal />
      <ShowGalleryPictureModal />
      <AddPicturesFromIncidentFormModal />
      <UpdateStatusWithNoteModal />
    </>
  );
};
